import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { ExamplePlugin } from "../../examples/types"

export const zeroConfigAnnouncementsPlugin: ExamplePlugin = {
  id: "zero-config-announcements",
  name: "Announcements",
  description: "Post updates & messages to keep your guests informed.",
  category: "Zero-Config",
  tags: ["zero-config", "announcements", "communication", "instant"],
  difficulty: "beginner",
  preview:
    "Instant announcements with glasomorphism design - no configuration needed",
  iconName: "lightning",
  showHostViewInDetails: true, // Show host view in event details
  requiresJoinToView: true, // Non-joined users cannot see announcements
  files: {
    [UserRole.GUEST]: `import React, { useState, useEffect } from 'react';

export default function GuestView({ pluginApi, pluginComponents, eventMetadata, userRole, isLoggedIn, eventId, pluginId }) {
  const {
    PluginContainer,
    LoadingSpinner
  } = pluginComponents || {};

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get event host name from available data
  const getHostName = () => {
    // First check if we have host info in announcements data
    if (announcements.length > 0 && announcements[0].hostName) {
      return announcements[0].hostName;
    }
    
    // Try to get from event metadata creator info
    if (eventMetadata?.creatorName) {
      return eventMetadata.creatorName;
    }
    
    // Try to get from event metadata host info
    if (eventMetadata?.hostInfo?.name) {
      return eventMetadata.hostInfo.name;
    }
    
    // For now, let's use a more generic approach and get it from the stored data
    return 'Event Host'; // We'll update this to get actual host name
  };

  // Load announcements on mount (with cache)
  useEffect(() => {
    let unsub = null;
    async function loadAnnouncements() {
      try {
        // Load from cache first
        const cached = await pluginComponents.getCachedPluginEventInstanceData(eventId, pluginId, (freshData) => {
          setAnnouncements(freshData?.announcements || []);
        });
        setAnnouncements(cached?.announcements || []);
      } catch (error) {
        console.error('Error loading announcements:', error);
      } finally {
        setLoading(false);
      }
    }
    loadAnnouncements();
    return () => { if (unsub) unsub(); };
  }, [eventId, pluginId]);

  const getPriorityBadge = (priority) => {
    if (priority === 'urgent') {
      return (
        <span className="px-2 py-1 bg-red-500 text-white text-xs font-medium rounded">
          Urgent
        </span>
      );
    } else if (priority === 'info') {
      return (
        <span className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded">
          Info
        </span>
      );
    }
    return null;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (loading) {
    return (
      <PluginContainer>
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner size="lg" />
        </div>
      </PluginContainer>
    );
  }

  return (
    <PluginContainer className="p-0">

      {/* Messages */}
      <div className="space-y-4 px-4 pb-4">
        {announcements.map((announcement) => (
          <div key={announcement.id} className="flex items-start gap-3">
                         {/* Avatar */}
             <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
               <span className="text-white/80 text-sm font-medium">
                 {getHostName().charAt(0)}
               </span>
             </div>
            
            {/* Message Content */}
            <div className="flex-1 min-w-0">
                             {/* Author and Priority Badge */}
               <div className="flex items-center gap-2 mb-1">
                 <span className="text-white/90 font-medium">
                   {getHostName()}
                 </span>
                 {getPriorityBadge(announcement.priority)}
               </div>
              
              {/* Message Text */}
              <p className="text-white/80 mb-2 text-left">
                {announcement.content}
              </p>
              
              {/* Timestamp aligned to right */}
              <div className="flex justify-end">
                <span className="text-white/50 text-sm">
                  {formatTime(announcement.timestamp)}
                </span>
              </div>
            </div>
          </div>
        ))}

        {announcements.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/60">No announcements yet. Check back later for updates!</p>
          </div>
        )}
      </div>
    </PluginContainer>
  );
}`,
    [UserRole.HOST_EVENT_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostView({ pluginApi, pluginComponents, eventMetadata, userRole, isLoggedIn, eventId, pluginId }) {
  const {
    PluginContainer,
    Heading,
    BaseCard,
    TextArea,
    PrimaryButton,
    Avatar,
    Timestamp,
    Badge,
    LoadingSpinner
  } = pluginComponents || {};

  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load announcements on mount (with cache)
  useEffect(() => {
    let unsub = null;
    async function loadAnnouncements() {
      try {
        // Load from cache first
        const cached = await pluginComponents.getCachedPluginEventInstanceData(eventId, pluginId, (freshData) => {
          setAnnouncements(freshData?.announcements || []);
        });
        setAnnouncements(cached?.announcements || []);
      } catch (error) {
        console.error('Error loading announcements:', error);
      } finally {
        setLoading(false);
      }
    }
    loadAnnouncements();
    return () => { if (unsub) unsub(); };
  }, [eventId, pluginId]);

  // Save new announcement
  const saveAnnouncement = async () => {
    if (!newAnnouncement.trim()) return;

    setSaving(true);
    try {
      // Get the actual host name from event metadata or user info
      const getActualHostName = () => {
        if (eventMetadata?.creatorName) return eventMetadata.creatorName;
        if (eventMetadata?.hostInfo?.name) return eventMetadata.hostInfo.name;
        return eventMetadata?.name || 'Event Host'; // Use event name as fallback
      };

      const announcement = {
        id: Date.now().toString(),
        content: newAnnouncement,
        priority: priority,
        timestamp: new Date(),
        authorId: 'current-user',
        authorName: 'You',
        hostName: getActualHostName() // Save the actual host name for guests to see
      };

      const updatedAnnouncements = [announcement, ...announcements];
      await pluginApi.savePluginEventInstanceData({
        announcements: updatedAnnouncements
      });
      // Update cache after saving
      pluginComponents.setCachedPluginEventInstanceData(eventId, pluginId, { announcements: updatedAnnouncements });
      setAnnouncements(updatedAnnouncements);
      setNewAnnouncement('');
      setPriority('normal');
    } catch (error) {
      console.error('Error saving announcement:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      const updatedAnnouncements = announcements.filter(a => a.id !== id);
      await pluginApi.savePluginEventInstanceData({
        announcements: updatedAnnouncements
      });
      // Update cache after deleting
      pluginComponents.setCachedPluginEventInstanceData(eventId, pluginId, { announcements: updatedAnnouncements });
      setAnnouncements(updatedAnnouncements);
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveAnnouncement();
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch(priority) {
      case 'urgent': return 'bg-red-500';
      case 'info': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <PluginContainer>
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner size="lg" />
        </div>
      </PluginContainer>
    );
  }

  return (
    <PluginContainer>
    

      {/* Messages Area */}
      <div className="flex flex-col h-96">
        {/* Announcements history */}
        <div className="flex-1 overflow-y-auto space-y-6 mb-6">
          {announcements.map((announcement) => (
            <div key={announcement.id} className="flex items-start gap-3">
              <Avatar size="sm" fallback={announcement.authorName.charAt(0)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white/90 font-medium">
                    {announcement.authorName}
                  </span>
                  {announcement.priority !== 'normal' && (
                    <span className={\`px-2 py-1 rounded text-xs text-white font-medium \${getPriorityBadgeColor(announcement.priority)}\`}>
                      {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)}
                    </span>
                  )}
                </div>
                <p className="text-white/80 mb-2 text-left">{announcement.content}</p>
                <div className="flex justify-end">
                  <Timestamp date={new Date(announcement.timestamp)} className="text-white/40 text-sm" />
                </div>
              </div>
            </div>
          ))}

          {announcements.length === 0 && (
            <div className="text-center py-12">
              <p className="text-white/60 mb-2">No announcements yet. Create your first announcement above!</p>
            </div>
          )}
        </div>

        {/* Line separator */}
        <div className="h-px bg-white/20 mb-4"></div>

        {/* Input Area */}
        <div className="space-y-3">
          {/* Avatar and text input on same line */}
          <div className="flex items-center gap-3">
            <Avatar size="sm" fallback="H" />
            <input
              type="text"
              value={newAnnouncement}
              onChange={(e) => setNewAnnouncement(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Send a blast to your guests..."
              className="flex-1 bg-transparent border-none outline-none px-4 py-3 text-white placeholder-white/60"
              disabled={saving}
            />
          </div>

          {/* Dropdown and Send button on next line */}
          <div className="flex items-center justify-between">
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="px-3 py-2 bg-transparent border-none outline-none text-white text-sm"
            >
              <option value="normal" className="bg-gray-800 text-white">Normal</option>
              <option value="urgent" className="bg-gray-800 text-white">Urgent</option>
              <option value="info" className="bg-gray-800 text-white">Info</option>
            </select>
            
            <button
              onClick={saveAnnouncement}
              disabled={!newAnnouncement.trim() || saving}
              className="flex items-center gap-2 px-4 py-2 bg-transparent text-white rounded-lg font-medium transition-colors"
            >
              <span>Send</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </PluginContainer>
  );
}`,
    [UserRole.HOST_CONFIG_VIEW]: `import React from 'react';

export default function HostConfigView({ pluginComponents }) {
  const { PluginContainer, Heading, BaseCard } = pluginComponents || {};

  return (
    <PluginContainer>
      <Heading level={3}>📢 Announcements Configuration</Heading>
      <BaseCard className="mt-4">
        <div className="text-center py-8">
          <p className="text-white/80 mb-2">🎉 Zero Configuration Required!</p>
          <p className="text-white/60 text-sm mb-4">
            Announcements are automatically displayed to guests. No setup needed!
          </p>
        </div>
      </BaseCard>
    </PluginContainer>
  );
}`,
  },
}
